import axios from 'axios';
import { Platform } from 'react-native';
import { API_CONFIG, TIMEOUTS } from '../constants/config';
import { getToken, saveToken, removeToken } from '../utils/storage';
import {
  createTokenRefreshInterceptor,
  getValidAccessToken,
  getAxiosAuthConfig,
} from '../utils/tokenInterceptor';

const RECENT_ANALYSIS_CACHE_KEY_PREFIX = 'recent_analysis_cache_';

export type SavedPaletteExtras = {
  hair_colors?: string[];
  eye_colors?: string[];
  jewelry?: Record<string, string>;
  skin_tone_category?: string;
  confidence?: number;
  analysis_type?: string;
  type?: string;
  colours?: Array<{
    rgb: [number, number, number];
    hex: string;
    percentage: number;
  }>;
};

export type RecentAnalysisResult = {
  id: number;
  name: string;
  image_url: string | null;
  dominant_colours: Array<{ rgb: number[]; hex: string; percentage: number }>;
  palette: SavedPaletteExtras;
  recommendations: Record<string, unknown>;
  recommended_colors: string[] | null;
  created_at: string;
  updated_at: string;
};

const getRecentAnalysisCacheKey = (userId: string) =>
  `${RECENT_ANALYSIS_CACHE_KEY_PREFIX}${userId}`;

export function getRecentAnalysisFromCacheSync(
  userId: string | number
): RecentAnalysisResult | null {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined')
    return null;
  try {
    const key = getRecentAnalysisCacheKey(String(userId));
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as RecentAnalysisResult;
  } catch {
    return null;
  }
}

const getRecentAnalysisFromCache = async (
  userId: string | null
): Promise<RecentAnalysisResult | null> => {
  if (!userId) return null;
  try {
    const key = getRecentAnalysisCacheKey(userId);
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      return JSON.parse(raw) as RecentAnalysisResult;
    }
    const stored = await getToken(key);
    if (!stored) return null;
    return JSON.parse(stored) as RecentAnalysisResult;
  } catch {
    return null;
  }
};

export const setRecentAnalysisCache = async (
  userId: string | null,
  data: RecentAnalysisResult
): Promise<void> => {
  if (!userId) return;
  try {
    const key = getRecentAnalysisCacheKey(userId);
    const serialized = JSON.stringify(data);
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, serialized);
      return;
    }
    await saveToken(key, serialized);
  } catch {
    // Silently fail - cache is non-critical
  }
};

export const clearRecentAnalysisCache = async (
  userId?: string | null
): Promise<void> => {
  try {
    if (userId) {
      const key = getRecentAnalysisCacheKey(userId);
      if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(key);
        return;
      }
      await removeToken(key);
      return;
    }
    if (Platform.OS === 'web' && typeof sessionStorage !== 'undefined') {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const k = sessionStorage.key(i);
        if (k && k.startsWith(RECENT_ANALYSIS_CACHE_KEY_PREFIX))
          keysToRemove.push(k);
      }
      keysToRemove.forEach((k) => sessionStorage.removeItem(k));
    }
  } catch {
    // Silently fail - cache clear is non-critical
  }
};

export interface ColourProfile {
  id: number;
  name: string;
  user_id: number;
  image_url?: string | null;
  dominant_colours: Array<{
    rgb: [number, number, number];
    hex: string;
    percentage: number;
  }>;
  palette: {
    type: string;
    colours?: Array<{
      rgb: [number, number, number];
      hex: string;
      percentage: number;
    }>;
    skin_tone_category?: string;
    confidence?: number;
    analysis_type?: string;
    hair_colors?: string[];
    eye_colors?: string[];
    jewelry?: Record<string, string>;
  };
  recommendations: {
    complementary_colours?: Array<{
      original: string;
      complementary: string;
    }>;
    clothing_suggestions: string[];
    undertone?: string;
    skin_tone_category?: string;
    confidence?: number;
  };
  recommended_colors?: string[]; // Array of hex color codes
  created_at: string;
  updated_at: string;
}

// Get current user from stored user data
const getCurrentUserId = async (): Promise<string | null> => {
  try {
    const userData = await getToken('user_data');
    if (userData) {
      const user = JSON.parse(userData);
      return user.id || user.email; // Use ID or email as fallback
    }
    return null;
  } catch {
    return null;
  }
};

// User-specific storage keys
const getUserColorProfilesKey = (userId: string) => `color_profiles_${userId}`;

// Get user-specific color profiles from storage
const getUserColorProfiles = async (
  userId: string
): Promise<ColourProfile[]> => {
  try {
    const userProfilesKey = getUserColorProfilesKey(userId);
    const storedProfiles = await getToken(userProfilesKey);

    if (storedProfiles) {
      return JSON.parse(storedProfiles);
    } else {
      // First time for this user, return empty array
      return [];
    }
  } catch {
    return [];
  }
};

// Save user-specific color profiles to storage
const saveUserColorProfiles = async (
  userId: string,
  profiles: ColourProfile[]
): Promise<void> => {
  try {
    const userProfilesKey = getUserColorProfilesKey(userId);
    await saveToken(userProfilesKey, JSON.stringify(profiles));
  } catch {
    // Silently fail
  }
};

// Generate realistic color profiles with variety
const generateColorProfile = (name: string, userId: string): ColourProfile => {
  // Define several color palette options
  const colorPalettes = [
    {
      type: 'warm',
      dominant_colours: [
        { rgb: [139, 69, 19], hex: '#8B4513', percentage: 45 },
        { rgb: [210, 180, 140], hex: '#D2B48C', percentage: 30 },
        { rgb: [160, 82, 45], hex: '#A0522D', percentage: 25 },
      ],
      palette_colours: [
        { rgb: [255, 140, 0], hex: '#FF8C00', percentage: 20 },
        { rgb: [255, 165, 0], hex: '#FFA500', percentage: 20 },
        { rgb: [255, 69, 0], hex: '#FF4500', percentage: 15 },
        { rgb: [218, 165, 32], hex: '#DAA520', percentage: 15 },
        { rgb: [184, 134, 11], hex: '#B8860B', percentage: 15 },
        { rgb: [205, 133, 63], hex: '#CD853F', percentage: 15 },
      ],
      complementary: [
        { original: '#FF8C00', complementary: '#0073FF' },
        { original: '#DAA520', complementary: '#255ADF' },
      ],
      clothing_suggestions: [
        'Warm earth tones',
        'Orange blazer',
        'Golden accessories',
        'Brown leather items',
      ],
    },
    {
      type: 'cool',
      dominant_colours: [
        { rgb: [70, 130, 180], hex: '#4682B4', percentage: 40 },
        { rgb: [135, 206, 235], hex: '#87CEEB', percentage: 35 },
        { rgb: [123, 104, 238], hex: '#7B68EE', percentage: 25 },
      ],
      palette_colours: [
        { rgb: [0, 191, 255], hex: '#00BFFF', percentage: 20 },
        { rgb: [30, 144, 255], hex: '#1E90FF', percentage: 18 },
        { rgb: [65, 105, 225], hex: '#4169E1', percentage: 16 },
        { rgb: [72, 61, 139], hex: '#483D8B', percentage: 16 },
        { rgb: [106, 90, 205], hex: '#6A5ACD', percentage: 15 },
        { rgb: [147, 112, 219], hex: '#9370DB', percentage: 15 },
      ],
      complementary: [
        { original: '#00BFFF', complementary: '#FF4000' },
        { original: '#4169E1', complementary: '#E14169' },
      ],
      clothing_suggestions: [
        'Cool blues and purples',
        'Navy blazer',
        'Silver accessories',
        'Denim items',
      ],
    },
    {
      type: 'neutral',
      dominant_colours: [
        { rgb: [128, 128, 128], hex: '#808080', percentage: 50 },
        { rgb: [169, 169, 169], hex: '#A9A9A9', percentage: 30 },
        { rgb: [105, 105, 105], hex: '#696969', percentage: 20 },
      ],
      palette_colours: [
        { rgb: [245, 245, 220], hex: '#F5F5DC', percentage: 18 },
        { rgb: [250, 240, 230], hex: '#FAF0E6', percentage: 17 },
        { rgb: [255, 248, 220], hex: '#FFF8DC', percentage: 16 },
        { rgb: [230, 230, 250], hex: '#E6E6FA', percentage: 16 },
        { rgb: [211, 211, 211], hex: '#D3D3D3', percentage: 16 },
        { rgb: [192, 192, 192], hex: '#C0C0C0', percentage: 17 },
      ],
      complementary: [
        { original: '#F5F5DC', complementary: '#0A0A23' },
        { original: '#E6E6FA', complementary: '#191905' },
      ],
      clothing_suggestions: [
        'Classic neutrals',
        'White button-down',
        'Gray accessories',
        'Beige outerwear',
      ],
    },
  ];

  // Randomly select a palette
  const selectedPalette =
    colorPalettes[Math.floor(Math.random() * colorPalettes.length)];

  return {
    id: Date.now(),
    name: name,
    user_id: parseInt(userId) || 1,
    image_url: null, // Add image_url field
    dominant_colours: selectedPalette.dominant_colours as Array<{
      rgb: [number, number, number];
      hex: string;
      percentage: number;
    }>,
    palette: {
      type: selectedPalette.type,
      colours: selectedPalette.palette_colours as Array<{
        rgb: [number, number, number];
        hex: string;
        percentage: number;
      }>,
    },
    recommendations: {
      complementary_colours: selectedPalette.complementary,
      clothing_suggestions: selectedPalette.clothing_suggestions,
    },
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
};

// Create axios instance with configuration
const api = axios.create({
  ...API_CONFIG,
  timeout: TIMEOUTS.API_REQUEST,
  ...getAxiosAuthConfig(),
});

// Add auth token to requests (skip when cookie mode - browser sends cookies)
api.interceptors.request.use(async (config) => {
  const token = await getValidAccessToken();
  if (token && token !== 'cookie') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle API errors and token refresh
api.interceptors.response.use(
  (response) => response,
  (error) => {
    throw error;
  }
);

// Add token refresh interceptor
createTokenRefreshInterceptor(api);

/**
 * Analyze an image and extract colour information
 * Now generates color recommendations frontend-side and saves to backend
 */
export const analyzeImage = async (
  imageUri: string,
  name: string
): Promise<ColourProfile> => {
  const userId = await getCurrentUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  // Generate color profile on frontend
  const newProfile = generateColorProfile(name, userId);

  // Always save to local storage first
  const existingProfiles = await getUserColorProfiles(userId);
  const updatedProfiles = [newProfile, ...existingProfiles];
  await saveUserColorProfiles(userId, updatedProfiles);

  // The backend only supports /analyze endpoint with image upload
  // Since we're doing frontend analysis, we'll just return the local profile
  return newProfile;
};

/**
 * Get all color profiles for the current user
 */
export const getColourProfiles = async (): Promise<ColourProfile[]> => {
  const userId = await getCurrentUserId();

  if (!userId) {
    return []; // No user, return empty array
  }

  // Check local storage first (most profiles are generated locally)
  const localProfiles = await getUserColorProfiles(userId);

  // Try to get backend profiles and merge with local ones
  try {
    const response = await api.get<ColourProfile[]>('/colours');
    const backendProfiles = response.data;

    // Merge profiles, avoiding duplicates (local profiles take precedence)
    const localIds = new Set(localProfiles.map((p) => p.id));
    const uniqueBackendProfiles = backendProfiles.filter(
      (p) => !localIds.has(p.id)
    );

    return [...localProfiles, ...uniqueBackendProfiles];
  } catch {
    return localProfiles;
  }
};

/**
 * Get a specific color profile by ID
 */
export const getColourProfile = async (
  profileId: number
): Promise<ColourProfile> => {
  const userId = await getCurrentUserId();

  if (!userId) {
    throw new Error('User not authenticated');
  }

  // Always check local storage first (most profiles are generated locally)
  const userProfiles = await getUserColorProfiles(userId);
  const localProfile = userProfiles.find(
    (p: ColourProfile) => p.id === profileId
  );

  // If found locally, return it (avoids unnecessary backend calls)
  if (localProfile) {
    return localProfile;
  }

  // Only try backend if not found locally
  try {
    const response = await api.get<ColourProfile>(`/colours/${profileId}`);
    return response.data;
  } catch {
    throw new Error('Color profile not found');
  }
};

/**
 * Delete a color profile
 */
export const deleteColourProfile = async (profileId: number): Promise<void> => {
  await api.delete(`/colours/${profileId}`);
};

/**
 * Save skin undertone analysis results to the backend
 */
export const saveSkinAnalysis = async (
  name: string,
  undertone: string,
  skinToneCategory: string,
  confidence: number,
  dominantColors: Array<{ r: number; g: number; b: number; hex: string }>,
  imageUri?: string | null
): Promise<ColourProfile> => {
  try {
    // Create form data for the backend
    const formData = new FormData();
    formData.append('name', name);
    formData.append('undertone', undertone);
    formData.append('skin_tone_category', skinToneCategory);
    formData.append('confidence', confidence.toString());
    formData.append('dominant_colors', JSON.stringify(dominantColors));

    // Add image if provided
    if (imageUri) {
      if (Platform.OS === 'web') {
        // For web, convert the image URI to a Blob
        try {
          const response = await fetch(imageUri);
          const blob = await response.blob();

          // Generate filename
          const originalFilename =
            imageUri.split('/').pop() || 'skin-analysis.jpg';
          const match = /\.(\w+)$/.exec(originalFilename);
          const extension = match ? `.${match[1]}` : '.jpg';
          const filename = `skin-analysis-${Date.now()}${extension}`;

          formData.append('image', blob, filename);
        } catch {
          // Continue without image if conversion fails
        }
      } else {
        // For React Native, append the image URI directly
        // @ts-ignore: FormData typings in React Native
        formData.append('image', {
          uri: imageUri,
          type: 'image/jpeg',
          name: `skin-analysis-${Date.now()}.jpg`,
        });
      }
    }

    const response = await api.post<ColourProfile>(
      '/colours/skin-analysis',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const userId = await getCurrentUserId();
    if (userId) {
      await setRecentAnalysisCache(
        userId,
        toRecentAnalysisResult(response.data)
      );
    }
    return response.data;
  } catch {
    throw new Error('Failed to save skin analysis to backend');
  }
};

/**
 * Save recommended colors and extended palette details to an existing colour profile
 */
export interface SaveRecommendedColorsOptions {
  hairColors?: string[];
  eyeColors?: string[];
  jewelry?: Record<string, string>;
}

export const saveRecommendedColors = async (
  profileId: number,
  recommendedColors: string[],
  extras?: SaveRecommendedColorsOptions
): Promise<ColourProfile> => {
  try {
    const formData = new FormData();
    formData.append('profile_id', profileId.toString());
    formData.append('recommended_colors', JSON.stringify(recommendedColors));
    if (extras?.hairColors && extras.hairColors.length > 0) {
      formData.append('hair_colors', JSON.stringify(extras.hairColors));
    }
    if (extras?.eyeColors && extras.eyeColors.length > 0) {
      formData.append('eye_colors', JSON.stringify(extras.eyeColors));
    }
    if (extras?.jewelry && Object.keys(extras.jewelry).length > 0) {
      formData.append('jewelry', JSON.stringify(extras.jewelry));
    }

    const response = await api.post<ColourProfile>(
      '/colours/recommended-colors',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const userId = await getCurrentUserId();
    if (userId) {
      await setRecentAnalysisCache(
        userId,
        toRecentAnalysisResult(response.data)
      );
    }
    return response.data;
  } catch {
    throw new Error('Failed to save recommended colors to backend');
  }
};

interface ProfileShape {
  id: number;
  name: string;
  image_url?: string | null;
  dominant_colours?: Array<{ rgb: number[]; hex: string; percentage: number }>;
  palette?: SavedPaletteExtras;
  recommendations?: Record<string, unknown>;
  recommended_colors?: string[] | null;
  created_at?: string;
  updated_at?: string;
}

const toRecentAnalysisResult = (
  profile: ProfileShape
): RecentAnalysisResult => ({
  id: profile.id,
  name: profile.name,
  image_url: profile.image_url ?? null,
  dominant_colours: profile.dominant_colours ?? [],
  palette: profile.palette ?? {},
  recommendations: profile.recommendations ?? {},
  recommended_colors: profile.recommended_colors ?? null,
  created_at: profile.created_at ?? '',
  updated_at: profile.updated_at ?? '',
});

export const getRecentAnalysis = async (): Promise<RecentAnalysisResult> => {
  const userId = await getCurrentUserId();
  const token = await getValidAccessToken();

  if (!token || !userId) {
    throw new Error('User not authenticated');
  }

  const cached = await getRecentAnalysisFromCache(userId);
  if (cached) {
    fetchRecentAnalysisAndUpdateCache(userId).catch(() => {});
    return cached;
  }

  return fetchRecentAnalysisAndUpdateCache(userId);
};

async function fetchRecentAnalysisAndUpdateCache(
  userId: string
): Promise<RecentAnalysisResult> {
  try {
    const response = await api.get('/colours/');
    const profiles = response.data;

    if (!profiles || profiles.length === 0) {
      throw new Error('No colour profiles found');
    }

    const sortedProfiles = profiles.sort((a: ProfileShape, b: ProfileShape) => {
      const aTime = new Date(a.created_at || a.updated_at || 0).getTime();
      const bTime = new Date(b.created_at || b.updated_at || 0).getTime();
      return bTime - aTime;
    });

    const mostRecent = sortedProfiles[0];
    const result = toRecentAnalysisResult(mostRecent);
    await setRecentAnalysisCache(userId, result);
    return result;
  } catch (err: unknown) {
    const localProfiles = await getUserColorProfiles(userId);
    if (localProfiles.length > 0) {
      const sorted = [...localProfiles].sort((a, b) => {
        const aTime = new Date(a.created_at || a.updated_at || 0).getTime();
        const bTime = new Date(b.created_at || b.updated_at || 0).getTime();
        return bTime - aTime;
      });
      const result = toRecentAnalysisResult(sorted[0]);
      await setRecentAnalysisCache(userId, result);
      return result;
    }

    const message =
      err instanceof Error
        ? err.message
        : (err as { message?: string })?.message;
    const status =
      err && typeof err === 'object' && 'response' in err
        ? (err.response as { status?: number })?.status
        : undefined;

    if (status === 401 || message?.includes('not authenticated')) {
      throw new Error('User not authenticated');
    }

    if (message?.includes('No colour profiles found')) {
      throw new Error('No colour profiles found');
    }

    throw new Error(message || 'Failed to retrieve recent analysis');
  }
}

// ─── Moonshot Kimi K2.5 AI colour recommendations ────────────────────────────

export interface AIRecommendationRequest {
  undertone: 'warm' | 'cool' | 'neutral';
  skinToneCategory: string;
  skinToneHex?: string | null;
  dominantColors: Array<{ hex: string }>;
}

export interface AIRecommendationResponse {
  recommendedColors: string[];
  avoidColors: string[];
  paletteName: string;
  description: string;
}

/**
 * Call the backend which proxies to Moonshot Kimi K2.5 to generate
 * personalised colour recommendations from already-detected skin data.
 * Throws on network or API errors so the caller can fall back to the
 * static palette.
 */
export const getAIColorRecommendations = async (
  payload: AIRecommendationRequest
): Promise<AIRecommendationResponse> => {
  const response = await api.post<AIRecommendationResponse>(
    '/colours/ai-recommendations',
    payload
  );
  return response.data;
};

// ─── Premium AI colour-insight text (batched sections) ───────────────────────

export interface AIPremiumTextSection {
  key: string;
  title: string;
  text: string;
}

export interface AIPremiumTextRequest {
  undertone: 'warm' | 'cool' | 'neutral';
  skinToneCategory: string;
  skinToneHex?: string | null;
  dominantColors: Array<{ hex: string }>;
}

export interface AIPremiumTextResponse {
  sections: AIPremiumTextSection[];
}

export const getAIPremiumColorText = async (
  payload: AIPremiumTextRequest
): Promise<AIPremiumTextResponse> => {
  const response = await api.post<AIPremiumTextResponse>(
    '/colours/ai-premium-text',
    payload
  );
  return response.data;
};

export interface AIChatReplyRequest {
  prompt: string;
  undertone?: 'warm' | 'cool' | 'neutral' | string;
  skinToneCategory?: string;
  dominantColors?: Array<{ hex: string }>;
}

export interface AIChatReplyResponse {
  prompt: string;
  reply: string;
}

export const getAIChatReply = async (
  payload: AIChatReplyRequest
): Promise<AIChatReplyResponse> => {
  const response = await api.post<AIChatReplyResponse>(
    '/colours/ai-chat-reply',
    payload
  );
  return response.data;
};

// ─── Seasonal two-stage classification ───────────────────────────────────────

export interface SeasonalClassificationRequest {
  dominantColors: Array<{ hex: string }>;
  undertone: 'warm' | 'cool' | 'neutral';
}

export interface SeasonalClassificationResponse {
  season: 'spring' | 'summer' | 'autumn' | 'winter';
  subtype: string;
  subtypeLabel: string;
  seasonConfidence: number;
  subtypeConfidence: number;
  topK: Array<{ subtype: string; confidence: number }>;
  recommendedColors: string[];
}

/**
 * Call the backend two-stage seasonal classifier.
 * Returns season + subtype predictions with confidence scores.
 */
export const getSeasonalClassification = async (
  payload: SeasonalClassificationRequest
): Promise<SeasonalClassificationResponse> => {
  const response = await api.post<SeasonalClassificationResponse>(
    '/colours/seasonal-classification',
    payload
  );
  return response.data;
};
